import React, { useState, useEffect } from "react";
import { Button, Alert, Table, Input } from "reactstrap";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { getConfig } from "../config";
import Loading from "../components/Loading";
import RoleBadge from "../components/RoleBadge";
import { listUserOrganisations, getOrganisation, readSelfProfile } from "../utils/apiService";
import { roleFor } from "../utils/orgRoles";
import { useHistory } from "react-router-dom";

export const OrganisationsComponent = () => {
  const { apiOrigin = "http://localhost:3001", audience } = getConfig();
  const history = useHistory();

  const [state, setState] = useState({
    showResult: false,
    organizations: [],
    filteredOrganizations: [],
    apiMessage: "",
    error: null,
    loading: false,
    searchTerm: "",
    sortField: null,
    sortDirection: "asc",
  });

  const { getAccessTokenSilently, loginWithPopup, getAccessTokenWithPopup } =
    useAuth0();

  // Auto-load organizations on component mount
  useEffect(() => {
    if (audience) {
      callApi();
    }
  }, [audience]);

  // Filter organizations based on search term
  useEffect(() => {
    if (!state.searchTerm) {
      setState(prev => ({ ...prev, filteredOrganizations: prev.organizations }));
    } else {
      const filtered = state.organizations.filter(org =>
        org.name.toLowerCase().includes(state.searchTerm.toLowerCase()) ||
        (org.identifier && org.identifier.toLowerCase().includes(state.searchTerm.toLowerCase())) ||
        (org.location && org.location.toLowerCase().includes(state.searchTerm.toLowerCase()))
      );
      setState(prev => ({ ...prev, filteredOrganizations: filtered }));
    }
  }, [state.organizations, state.searchTerm]);

  const handleSort = (field) => {
    const direction = state.sortField === field && state.sortDirection === "asc" ? "desc" : "asc";
    const sorted = [...state.filteredOrganizations].sort((a, b) => {
      const aVal = a[field] || '';
      const bVal = b[field] || '';
      if (direction === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setState(prev => ({
      ...prev,
      filteredOrganizations: sorted,
      sortField: field,
      sortDirection: direction
    }));
  };

  const handleSearch = (e) => {
    setState(prev => ({ ...prev, searchTerm: e.target.value }));
  };

  const handleConsent = async () => {
    try {
      await getAccessTokenWithPopup();
      setState({
        ...state,
        error: null,
      });
    } catch (error) {
      setState({
        ...state,
        error: error.error,
      });
    }

    await callApi();
  };

  const handleLoginAgain = async () => {
    try {
      await loginWithPopup();
      setState({
        ...state,
        error: null,
      });
    } catch (error) {
      setState({
        ...state,
        error: error.error,
      });
    }

    await callApi();
  };

  const callApi = async () => {
    setState({
      ...state,
      loading: true,
      error: null,
    });

    try {
      let token = null;
      try {
        token = await getAccessTokenSilently();
      } catch (e) {
        token = null;
      }

      const [apiData, self] = await Promise.all([listUserOrganisations(token), readSelfProfile(token)]);

      // Read_All_User_Organisations answers a DCR_OrgCompleteList: the organisations are in completeOrgList.
      const list = Array.isArray(apiData)
        ? apiData
        : (apiData?.completeOrgList || apiData?.data || apiData?.items || apiData?.organisations || apiData?.organizations || []);

      // The list leaves the creator and location unfilled (-1), so read each organisation for them;
      // a user has at most 20. One that cannot be read still shows, without edit controls.
      const details = await Promise.all((list || []).map((item) =>
        item?.orgId > 0 ? getOrganisation(item.orgId, token).catch(() => null) : Promise.resolve(null)));

      const normalized = (list || []).map((item, index) => {
        const full = details[index] || item;
        const id = item?.orgId || item?.id || item?.organisationId || item?.organizationId || index + 1;
        const name = full?.orgName || full?.name || full?.organisationName || full?.organizationName || full?.title || `Organisation ${id}`;
        const identifier = full?.orgIdentifier || full?.identifier || full?.organizationIdentifier || '';
        const unfilled = full?.latitude === -1 && full?.longitude === -1;
        const lat = unfilled ? 0 : (full?.latitude || 0);
        const lng = unfilled ? 0 : (full?.longitude || 0);
        const location = (lat !== 0 || lng !== 0) ? `${lat}, ${lng}` : '—';
        const memberCount = Number(item?.memberCount || item?.members?.length || 0) || 0;
        const userRole = roleFor(full, self?.userId);
        return { id, name, identifier, location, memberCount, userRole };
      });

      setState((prev) => ({
        ...prev,
        showResult: true,
        organizations: normalized,
        filteredOrganizations: normalized,
        apiMessage: apiData,
        loading: false,
      }));
    } catch (error) {
      setState({
        ...state,
        error: error?.message || error?.error || "unknown_error",
        loading: false,
      });
    }
  };

  const open = (org) => history.push(`/organisations/${org.id}`);

  const handle = (e, fn) => {
    e.preventDefault();
    fn();
  };

  const sortIndicator = (field) => (
    <span className={`sort-indicator${state.sortField === field ? " active" : ""}`}>
      {state.sortField !== field ? "↕" : state.sortDirection === "asc" ? "↑" : "↓"}
    </span>
  );

  const count = state.organizations.length;

  return (
    <div style={{ paddingBottom: "3rem" }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">Organisations</h2>
          <p className="page-subtitle">The businesses you run on Wideby, their location and their offers</p>
        </div>
        <Button color="primary" onClick={() => history.push('/organisations/create')}>
          + Create organisation
        </Button>
      </div>

      {state.error === "consent_required" && (
        <Alert color="warning" className="mb-3">
          You need to{" "}
          <a href="#/" className="alert-link" onClick={(e) => handle(e, handleConsent)}>
            consent to get access to users api
          </a>
        </Alert>
      )}

      {state.error === "login_required" && (
        <Alert color="warning" className="mb-3">
          You need to{" "}
          <a href="#/" className="alert-link" onClick={(e) => handle(e, handleLoginAgain)}>
            log in again
          </a>
        </Alert>
      )}

      {state.error && state.error !== "consent_required" && state.error !== "login_required" && (
        <Alert color="danger" className="mb-3">{state.error}</Alert>
      )}

      {!audience && (
        <Alert color="warning" className="mb-3">
          <p>
            You can't call the API at the moment because your application does
            not have any configuration for <code>audience</code>, or it is
            using the default value of{" "}
            <code>&#123;yourApiIdentifier&#125;</code>. Set the audience in{" "}
            <code>auth_config.json</code> and restart the app.
          </p>
        </Alert>
      )}

      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">
            {state.loading ? "Loading…" : `${count} organisation${count === 1 ? "" : "s"}`}
          </div>
          {count > 0 && (
            <div className="panel-search">
              <span className="panel-search-icon">🔍</span>
              <Input
                placeholder="Search organisations…"
                value={state.searchTerm}
                onChange={handleSearch}
              />
            </div>
          )}
        </div>

        {state.loading && !state.showResult ? (
          <div className="loading-state">
            <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
            <div>Loading organisations…</div>
          </div>
        ) : (state.showResult || audience) && (
          <div data-testid="api-result">
            <Table responsive className="data-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort("name")}>Name{sortIndicator("name")}</th>
                  <th className="sortable" onClick={() => handleSort("identifier")}>Identifier{sortIndicator("identifier")}</th>
                  <th className="sortable" onClick={() => handleSort("location")}>Location{sortIndicator("location")}</th>
                  <th>Members</th>
                  <th>Role</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {state.filteredOrganizations.length > 0 ? (
                  state.filteredOrganizations.map((org) => (
                    <tr key={org.id} className="row-link" onClick={() => open(org)}>
                      <td>
                        <a
                          href={`/organisations/${org.id}`}
                          className="cell-strong"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); open(org); }}
                        >
                          {org.name}
                        </a>
                      </td>
                      <td>
                        {org.identifier ? <span className="tag tag-grey">{org.identifier}</span> : <span className="cell-faint">—</span>}
                      </td>
                      <td className="cell-muted">{org.location}</td>
                      <td className="cell-muted">{org.memberCount}</td>
                      <td><RoleBadge role={org.userRole} /></td>
                      <td className="text-right">
                        <span className="row-open" aria-hidden="true">Open ›</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">
                      {state.searchTerm ? (
                        <div className="empty-state">
                          <div className="empty-icon">🔍</div>
                          <div className="empty-title">No results for “{state.searchTerm}”</div>
                          <div className="empty-text">Try a different search term</div>
                        </div>
                      ) : (
                        <div className="empty-state">
                          <div className="empty-icon">🏪</div>
                          <div className="empty-title">No organisations yet</div>
                          <div className="empty-text">Add your business to start publishing offers to people nearby.</div>
                          <Button color="primary" size="sm" onClick={() => history.push('/organisations/create')}>
                            + Create organisation
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default withAuthenticationRequired(OrganisationsComponent, {
  onRedirecting: () => <Loading />,
});
